import goodKill from "assets/settings/goodKill.png";
import insaneKill from "assets/settings/insaneKill.png";
import LoadError from "components/LoadError";
import Settings from "components/Settings";
import Loader from "components/common/Loader";
import ChannelInput from "components/dashboard/ChannelInput";
import SettingHelp from "components/dashboard/SettingHelp";
import { useAppDispatch, useAppSelector } from "helpers/hooks";
import { isSubscriptionActive } from "helpers/subscriptions";
import { capitalize } from "helpers/utils";
import { Button, Col, Form, Row, Stack } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
  useFetchServerQuery,
  useGetConstantsQuery,
  useTestNotificationSettingsMutation,
} from "store/api";
import { useGetServerSubscriptionQuery } from "store/api/server";
import {
  setJuicyChannel,
  setJuicyEnabled,
  setJuicyMode,
  setJuicyProvider,
} from "store/settings";

const JuicyPage = () => {
  const { serverId = "" } = useParams();
  const dispatch = useAppDispatch();

  const constants = useGetConstantsQuery();
  const server = useFetchServerQuery(serverId);
  const subscription = useGetServerSubscriptionQuery({ serverId });

  const juicy = useAppSelector((state) => state.settings.juicy);
  const [dispatchTestNotification, testNotification] =
    useTestNotificationSettingsMutation();

  if (server.isFetching || constants.isFetching || subscription.isFetching) {
    return <Loader />;
  }

  if (!server.data || !constants.data) {
    return <LoadError />;
  }

  const { modes, providers } = constants.data;
  const { channels } = server.data;

  const isPremium =
    subscription.data && isSubscriptionActive(subscription.data);

  return (
    <Settings
      alerts={[
        {
          show: !isPremium,
          variant: "danger",
          message: (
            <>
              This is a Premium feature. To enable, please check the
              <Link to="/premium"> Premium</Link> page to buy assign a
              subscription.
            </>
          ),
        },
      ]}
    >
      <Stack gap={2}>
        {constants.data.servers.map((server) => (
          <Form.Group controlId={`juicy-enabled-${server.id}`}>
            <Form.Check
              type="switch"
              label={server.name}
              checked={juicy.enabled[server.id]}
              disabled={!isPremium}
              onChange={(e) =>
                dispatch(
                  setJuicyEnabled({
                    serverId: server.id,
                    enabled: e.target.checked,
                  })
                )
              }
            />
          </Form.Group>
        ))}

        <Row className="g-2 align-items-end">
          <Col xs={12} md={true}>
            <Form.Group controlId="good-channel">
              <Form.Label>
                <Stack direction="horizontal" gap={1}>
                  <div>Good Kills Notification Channel</div>
                  <SettingHelp
                    title="Good Kills"
                    imageSrc={goodKill}
                    imageAlt="Example of Good Kill"
                  >
                    Good kills are kills whose loot is worth a certain threshold
                    (roughly around ~15m).
                  </SettingHelp>
                </Stack>
              </Form.Label>
              <ChannelInput
                aria-label="Good kills notification channel"
                disabled={!isPremium}
                availableChannels={channels}
                value={juicy.good.channel}
                onChannelChange={(channelId) =>
                  dispatch(
                    setJuicyChannel({ type: "good", channel: channelId })
                  )
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md="auto">
            <Button
              disabled={!isPremium || testNotification.isLoading}
              variant="secondary"
              type="button"
              onClick={() => {
                dispatchTestNotification({
                  serverId,
                  type: "good",
                  channelId: juicy.good.channel,
                  mode: juicy.mode,
                });
              }}
            >
              Test Notification
            </Button>
          </Col>
        </Row>

        <Row className="g-2 align-items-end">
          <Col xs={12} md={true}>
            <Form.Group controlId="insane-channel">
              <Form.Label>
                <Stack direction="horizontal" gap={1}>
                  <div>Insane Kills Notification Channel</div>
                  <SettingHelp
                    title="Insane Kills"
                    imageSrc={insaneKill}
                    imageAlt="Example of Insane Kill"
                  >
                    Insane kills are the most expensive kills on the entire
                    server. The loot value has no limits but is expected to be
                    at least ~30m.
                  </SettingHelp>
                </Stack>
              </Form.Label>
              <ChannelInput
                aria-label="Insane kills notification channel"
                disabled={!isPremium}
                availableChannels={channels}
                value={juicy.insane.channel}
                onChannelChange={(channelId) =>
                  dispatch(
                    setJuicyChannel({ type: "insane", channel: channelId })
                  )
                }
              />
            </Form.Group>
          </Col>
          <Col xs={12} md="auto">
            <Button
              disabled={!isPremium || testNotification.isLoading}
              variant="secondary"
              type="button"
              onClick={() => {
                dispatchTestNotification({
                  serverId,
                  type: "insane",
                  channelId: juicy.insane.channel,
                  mode: juicy.mode,
                });
              }}
            >
              Test Notification
            </Button>
          </Col>
        </Row>

        <Form.Group controlId="juicy-mode">
          <Form.Label>Mode</Form.Label>
          <Form.Select
            aria-label="Notification mode"
            disabled={!isPremium}
            value={juicy.mode}
            onChange={(e) => dispatch(setJuicyMode(e.target.value))}
          >
            {modes.map((mode) => (
              <option key={mode} value={mode}>
                {capitalize(mode)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="juicy-provider">
          <Form.Label>Link Provider</Form.Label>
          <Form.Select
            aria-label="Links provider"
            disabled={!isPremium}
            value={juicy.provider}
            onChange={(e) => dispatch(setJuicyProvider(e.target.value))}
          >
            {providers
              .filter((provider) => provider.events)
              .map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
          </Form.Select>
        </Form.Group>
      </Stack>
    </Settings>
  );
};

export default JuicyPage;
